/* ==========================================================================
   F3 Fleet — icon set
   --------------------------------------------------------------------------
   One set, one grid, one hand. Inline SVG, no font, no dependency, no second
   source: nothing anywhere in this app is allowed to draw a glyph except this
   file. (That rule is load-bearing — the moment a select caret is a CSS
   data-URI, two carets on the same screen disagree about weight and colour.)

   THE GRID
     16×16 viewBox. Every axis-aligned stroke — every rect edge, every rule,
     every tick — sits on a half-integer, so a 1.25px band puts ~90% of its
     ink inside one device pixel instead of spreading two grey ones. Diagonals
     and arcs anti-alias, which is correct; horizontals and verticals must not.
     Most glyphs are built around the pixel centre (7.5, 7.5) rather than the
     box centre (8, 8) for exactly this reason.

   TIER 1 · HAIRLINE — the default, and all but four glyphs
     stroke 1.25px, fill none, currentColor. No closed counter in this tier is
     narrower than 2.2px at 16px, so a small shape never fills in and sits as a
     mass beside its hairline neighbours.

   TIER 2 · SOLID — four glyphs, listed in SOLID below and nowhere else
     play · pause · stop · record
     Filled, no stroke, inside a 7×7 box on integer edges (4→11). These are
     transport marks: a hollow "stop" is a selection rectangle and a hollow
     "record" is just a circle, so outlining them would cost the meaning. The
     box is sized so a solid glyph carries roughly the ink of a 12px hairline
     outline and the two tiers sit at the same optical weight.

   The only filled element permitted inside a TIER 1 glyph is a dot of r ≤ 1.05
   standing for a literal printed dot: the point of an "i" or a "!", an
   ellipsis, a list bullet, a graph node, a lens centre, a keyhole.

   WHAT EARNS A NAME
     Every glyph here is an affordance (expand, filter, overflow, lock) or a
     state (live antenna, fault octagon, stale clock). There is no "robot"
     glyph, no section ornament and no synonym: an icon that sits next to a
     word already saying the same thing is noise, and two glyphs for one idea
     are two chances to pick the wrong one. `route`, `plot` and `telemetry`
     were three trend lines; `commands` and `terminal` were one prompt drawn
     twice; `grid` and `fleet` were the same four cells; `catalog` and
     `maintenance` were the same hex nut. Each pair is now one name.
   ========================================================================== */

import { useId } from 'react';

/** A filled dot standing for a printed dot. The only fill allowed in tier 1. */
const D = { fill: 'currentColor', stroke: 'none' } as const;

const PATHS = {
  /* ---- workspaces -----------------------------------------------------
     These four appear in the 48px collapsed nav rail and nowhere else — the
     labelled rail sets the word, and a word plus a picture of the word is
     one of them doing no work. So they only have to be four things nobody
     confuses at 16px: a matrix, a trace, a prompt, a chip.               */
  fleet: (
    <>
      <rect x="1.5" y="1.5" width="5" height="5" />
      <rect x="8.5" y="1.5" width="5" height="5" />
      <rect x="1.5" y="8.5" width="5" height="5" />
      <rect x="8.5" y="8.5" width="5" height="5" />
    </>
  ),
  telemetry: <polyline points="1.5,7.5 4,7.5 6,3.5 9,11.5 11,7.5 13.5,7.5" />,
  commands: (
    <>
      <polyline points="2.5,3.5 6.5,7.5 2.5,11.5" />
      <line x1="8.5" y1="11.5" x2="13.5" y2="11.5" />
    </>
  ),
  /* A hex nut. The catalogue defines *hardware* — morphologies, revisions,
     the Orin/MID-360/OAK-D that hang off them — and a hexagon is the only
     silhouette in this set that says "a machined part". A DIP package was
     tried first and lost: with eight legs it read as an asterisk at 16px and
     with four it read as a handbag. */
  catalog: (
    <>
      <polygon points="7.5,1.5 12.5,4.5 12.5,10.5 7.5,13.5 2.5,10.5 2.5,4.5" />
      <circle cx="7.5" cy="7.5" r="2" />
    </>
  ),

  /* ---- panels & layout ------------------------------------------------ */
  'panel-left': (
    <>
      <rect x="1.5" y="2.5" width="12" height="10" />
      <line x1="5.5" y1="2.5" x2="5.5" y2="12.5" />
    </>
  ),
  'panel-right': (
    <>
      <rect x="1.5" y="2.5" width="12" height="10" />
      <line x1="9.5" y1="2.5" x2="9.5" y2="12.5" />
    </>
  ),
  columns: (
    <>
      <rect x="1.5" y="2.5" width="12" height="10" />
      <line x1="5.5" y1="2.5" x2="5.5" y2="12.5" />
      <line x1="9.5" y1="2.5" x2="9.5" y2="12.5" />
    </>
  ),
  list: (
    <>
      <circle cx="2.5" cy="3.5" r="1" {...D} />
      <circle cx="2.5" cy="7.5" r="1" {...D} />
      <circle cx="2.5" cy="11.5" r="1" {...D} />
      <line x1="5.5" y1="3.5" x2="13.5" y2="3.5" />
      <line x1="5.5" y1="7.5" x2="13.5" y2="7.5" />
      <line x1="5.5" y1="11.5" x2="13.5" y2="11.5" />
    </>
  ),
  /* Two planes, not three: at 16px a third plane closes the 1.75px gaps and
     the whole thing reads as a filled lozenge. */
  layers: (
    <>
      <polygon points="7.5,1.5 13.5,5.5 7.5,9.5 1.5,5.5" />
      <polyline points="1.5,8.5 7.5,12.5 13.5,8.5" />
    </>
  ),
  expand: (
    <>
      <polyline points="9.5,2.5 12.5,2.5 12.5,5.5" />
      <polyline points="5.5,12.5 2.5,12.5 2.5,9.5" />
      <line x1="12.5" y1="2.5" x2="8.5" y2="6.5" />
      <line x1="2.5" y1="12.5" x2="6.5" y2="8.5" />
    </>
  ),
  collapse: (
    <>
      <polyline points="12.5,6.5 8.5,6.5 8.5,2.5" />
      <polyline points="2.5,8.5 6.5,8.5 6.5,12.5" />
      <line x1="8.5" y1="6.5" x2="12.5" y2="2.5" />
      <line x1="6.5" y1="8.5" x2="2.5" y2="12.5" />
    </>
  ),
  cube: (
    <>
      <polygon points="7.5,1.5 13.5,5 13.5,10 7.5,13.5 1.5,10 1.5,5" />
      <polyline points="1.5,5 7.5,8.5 13.5,5" />
      <line x1="7.5" y1="8.5" x2="7.5" y2="13.5" />
    </>
  ),

  /* ---- spatial -------------------------------------------------------- */
  map: (
    <>
      <polygon points="1.5,4.5 5.5,2.5 9.5,4.5 13.5,2.5 13.5,10.5 9.5,12.5 5.5,10.5 1.5,12.5" />
      <line x1="5.5" y1="2.5" x2="5.5" y2="10.5" />
      <line x1="9.5" y1="4.5" x2="9.5" y2="12.5" />
    </>
  ),
  waypoint: (
    <>
      <path d="M7.5 13.5s4-4.7 4-8a4 4 0 1 0-8 0c0 3.3 4 8 4 8Z" />
      <circle cx="7.5" cy="5.5" r="2" />
    </>
  ),
  compass: (
    <>
      <circle cx="7.5" cy="7.5" r="6" />
      <polygon points="10.4,4.6 8.9,8.9 4.6,10.4 6.1,6.1" />
    </>
  ),
  crosshair: (
    <>
      <circle cx="7.5" cy="7.5" r="4" />
      <line x1="7.5" y1="1.5" x2="7.5" y2="3.5" />
      <line x1="7.5" y1="11.5" x2="7.5" y2="13.5" />
      <line x1="1.5" y1="7.5" x2="3.5" y2="7.5" />
      <line x1="11.5" y1="7.5" x2="13.5" y2="7.5" />
      <circle cx="7.5" cy="7.5" r="1" {...D} />
    </>
  ),
  scan: (
    <>
      <polyline points="1.5,5.5 1.5,1.5 5.5,1.5" />
      <polyline points="9.5,1.5 13.5,1.5 13.5,5.5" />
      <polyline points="13.5,9.5 13.5,13.5 9.5,13.5" />
      <polyline points="5.5,13.5 1.5,13.5 1.5,9.5" />
      <line x1="3.5" y1="7.5" x2="11.5" y2="7.5" />
    </>
  ),
  measure: (
    <>
      <rect x="1.5" y="4.5" width="12" height="6" />
      <line x1="4.5" y1="4.5" x2="4.5" y2="6.5" />
      <line x1="7.5" y1="4.5" x2="7.5" y2="6.5" />
      <line x1="10.5" y1="4.5" x2="10.5" y2="6.5" />
    </>
  ),

  /* ---- health & vitals ------------------------------------------------- */
  /* One closed path, so the terminal is part of the cell rather than a stub
     floating 2px off it. Every edge lands on a half-integer. */
  battery: <path d="M1.5 4.5h9v2h2v2h-2v2h-9Z" />,
  plug: (
    <>
      <line x1="5.5" y1="1.5" x2="5.5" y2="4.5" />
      <line x1="9.5" y1="1.5" x2="9.5" y2="4.5" />
      <path d="M3.5 4.5h8V7a4 4 0 0 1-8 0Z" />
      <line x1="7.5" y1="11" x2="7.5" y2="13.5" />
    </>
  ),
  thermometer: (
    <>
      <path d="M9.5 8.6V3.5a2 2 0 0 0-4 0v5.1a3 3 0 1 0 4 0Z" />
      <line x1="7.5" y1="5.5" x2="7.5" y2="10.5" />
    </>
  ),
  disk: (
    <>
      <ellipse cx="7.5" cy="4" rx="5" ry="2.5" />
      <path d="M2.5 4v7.5c0 1.38 2.24 2.5 5 2.5s5-1.12 5-2.5V4" />
      <path d="M2.5 8c0 1.38 2.24 2.5 5 2.5s5-1.12 5-2.5" />
    </>
  ),
  power: (
    <>
      <line x1="7.5" y1="1.5" x2="7.5" y2="7.5" />
      <path d="M4.2 4.2a4.6 4.6 0 1 0 6.6 0" />
    </>
  ),
  shield: <path d="M7.5 1.5 12.5 3.6v4.2c0 3.4-2.2 5.4-5 6.2-2.8-.8-5-2.8-5-6.2V3.6Z" />,
  key: (
    <>
      <circle cx="4.5" cy="10.5" r="2.5" />
      <line x1="6.3" y1="8.7" x2="13.5" y2="1.5" />
      <line x1="10.4" y1="4.6" x2="12" y2="6.2" />
    </>
  ),
  clock: (
    <>
      <circle cx="7.5" cy="7.5" r="6" />
      <polyline points="7.5,4 7.5,7.8 10.2,9.2" />
    </>
  ),
  history: (
    <>
      <path d="M1.9 7.5a5.6 5.6 0 1 0 1.7-4" />
      <polyline points="1.9,1.5 1.9,5 5.4,5" />
      <polyline points="7.5,5 7.5,7.8 10,9.2" />
    </>
  ),
  'alert-triangle': (
    <>
      <polygon points="7.5,1.5 13.5,13.5 1.5,13.5" />
      <line x1="7.5" y1="6" x2="7.5" y2="9.5" />
      <circle cx="7.5" cy="11.6" r="1" {...D} />
    </>
  ),
  'alert-octagon': (
    <>
      <polygon points="5.2,1.5 9.8,1.5 13.5,5.2 13.5,9.8 9.8,13.5 5.2,13.5 1.5,9.8 1.5,5.2" />
      <line x1="7.5" y1="4.2" x2="7.5" y2="8.2" />
      <circle cx="7.5" cy="10.5" r="1" {...D} />
    </>
  ),
  check: <polyline points="2.5,8 6,11.5 12.5,3.5" />,
  'check-circle': (
    <>
      <circle cx="7.5" cy="7.5" r="6" />
      <polyline points="4.4,7.7 6.6,9.9 10.6,5.4" />
    </>
  ),
  close: (
    <>
      <line x1="3.5" y1="3.5" x2="11.5" y2="11.5" />
      <line x1="11.5" y1="3.5" x2="3.5" y2="11.5" />
    </>
  ),
  'x-circle': (
    <>
      <circle cx="7.5" cy="7.5" r="6" />
      <line x1="5.3" y1="5.3" x2="9.7" y2="9.7" />
      <line x1="9.7" y1="5.3" x2="5.3" y2="9.7" />
    </>
  ),
  refresh: (
    <>
      <path d="M13.5 7.5a6 6 0 1 1-1.9-4.4" />
      <polyline points="13.5,1.5 13.5,5 10,5" />
    </>
  ),

  /* ---- connectivity ----------------------------------------------------
     Every `-off` variant is its affirmative twin, unedited, plus the one cut
     described under NEGATION below. Nothing is redrawn for the off state and
     no pair diverges: the reason `antenna` carries one wave pair rather than
     two is precisely that two leave no room for the cut.                   */
  signal: (
    <>
      <line x1="1.5" y1="13.5" x2="1.5" y2="11" />
      <line x1="5.5" y1="13.5" x2="5.5" y2="8" />
      <line x1="9.5" y1="13.5" x2="9.5" y2="5" />
      <line x1="13.5" y1="13.5" x2="13.5" y2="2" />
    </>
  ),
  'signal-off': (
    <>
      <line x1="1.5" y1="13.5" x2="1.5" y2="11" />
      <line x1="5.5" y1="13.5" x2="5.5" y2="8" />
      <line x1="9.5" y1="13.5" x2="9.5" y2="5" />
      <line x1="13.5" y1="13.5" x2="13.5" y2="2" />
    </>
  ),
  /* One wave pair, not two. Two pairs leave 1.25px of air between the arcs and
     survive on their own, but there is no room left for the cut that makes
     `antenna-off` — and an affirmative glyph whose negation cannot be drawn is
     the wrong affirmative glyph. */
  antenna: (
    <>
      <circle cx="7.5" cy="7.5" r="1.05" {...D} />
      <path d="M3.96 3.96a5 5 0 0 0 0 7.08" />
      <path d="M11.04 11.04a5 5 0 0 0 0-7.08" />
    </>
  ),
  'antenna-off': (
    <>
      <circle cx="7.5" cy="7.5" r="1.05" {...D} />
      <path d="M3.96 3.96a5 5 0 0 0 0 7.08" />
      <path d="M11.04 11.04a5 5 0 0 0 0-7.08" />
    </>
  ),
  link: (
    <>
      <path d="M6 9a2.5 2.5 0 0 0 3.5 0l2-2a2.5 2.5 0 0 0-3.5-3.5l-1 1" />
      <path d="M9 6a2.5 2.5 0 0 0-3.5 0l-2 2a2.5 2.5 0 0 0 3.5 3.5l1-1" />
    </>
  ),
  'link-off': (
    <>
      <path d="M6 9a2.5 2.5 0 0 0 3.5 0l2-2a2.5 2.5 0 0 0-3.5-3.5l-1 1" />
      <path d="M9 6a2.5 2.5 0 0 0-3.5 0l-2 2a2.5 2.5 0 0 0 3.5 3.5l1-1" />
    </>
  ),

  /* ---- transport (TIER 2 · solid) -------------------------------------- */
  play: <polygon points="4,3 12,7.5 4,12" {...D} />,
  pause: (
    <>
      <rect x="4" y="3" width="2" height="9" {...D} />
      <rect x="9" y="3" width="2" height="9" {...D} />
    </>
  ),
  stop: <rect x="4" y="4" width="7" height="7" {...D} />,
  record: <circle cx="7.5" cy="7.5" r="3.5" {...D} />,

  /* ---- commands & capture --------------------------------------------- */
  home: (
    <>
      <polyline points="1.5,7.5 7.5,2.5 13.5,7.5" />
      <polyline points="3.5,6.3 3.5,13.5 11.5,13.5 11.5,6.3" />
    </>
  ),
  camera: (
    <>
      <path d="M1.5 5.5h2.8l1.3-2h4.8l1.3 2h1.8v8h-12Z" />
      <circle cx="7.5" cy="9.5" r="2.5" />
    </>
  ),
  video: (
    <>
      <rect x="1.5" y="4.5" width="9" height="8" />
      <polygon points="10.5,8.5 13.5,6 13.5,11" />
    </>
  ),
  'video-off': (
    <>
      <rect x="1.5" y="4.5" width="9" height="8" />
      <polygon points="10.5,8.5 13.5,6 13.5,11" />
    </>
  ),
  lock: (
    <>
      <rect x="2.5" y="6.5" width="10" height="7" />
      <path d="M5 6.5V4.5a2.5 2.5 0 0 1 5 0v2" />
      <circle cx="7.5" cy="10" r="1" {...D} />
    </>
  ),
  unlock: (
    <>
      <rect x="2.5" y="6.5" width="10" height="7" />
      <path d="M5 6.5V4.5a2.5 2.5 0 0 1 4.8-.9" />
      <circle cx="7.5" cy="10" r="1" {...D} />
    </>
  ),
  upload: (
    <>
      <polyline points="4.5,5 7.5,2 10.5,5" />
      <line x1="7.5" y1="2" x2="7.5" y2="10" />
      <polyline points="2.5,10.5 2.5,13.5 12.5,13.5 12.5,10.5" />
    </>
  ),
  download: (
    <>
      <polyline points="4.5,7 7.5,10 10.5,7" />
      <line x1="7.5" y1="2" x2="7.5" y2="10" />
      <polyline points="2.5,10.5 2.5,13.5 12.5,13.5 12.5,10.5" />
    </>
  ),

  /* ---- objects --------------------------------------------------------- */
  folder: <path d="M1.5 13.5v-11h4.2l1.6 2h6.2v9Z" />,
  file: (
    <>
      <path d="M3.5 1.5h5l3 3v9h-8Z" />
      <polyline points="8.5,1.5 8.5,4.5 11.5,4.5" />
    </>
  ),
  container: (
    <>
      <rect x="1.5" y="4.5" width="12" height="8" />
      <line x1="1.5" y1="7.5" x2="13.5" y2="7.5" />
      <line x1="5.5" y1="4.5" x2="5.5" y2="7.5" />
      <line x1="9.5" y1="4.5" x2="9.5" y2="7.5" />
    </>
  ),
  tag: (
    <>
      <path d="M1.5 7.9V2.5h5.4l6.6 6.6-5.4 5.4Z" />
      <circle cx="4.5" cy="5.5" r="1" {...D} />
    </>
  ),
  flag: (
    <>
      <line x1="3.5" y1="1.5" x2="3.5" y2="13.5" />
      <path d="M3.5 2.5h9l-1.9 3 1.9 3h-9Z" />
    </>
  ),
  branch: (
    <>
      <circle cx="4.5" cy="3.5" r="2" />
      <circle cx="4.5" cy="11.5" r="2" />
      <circle cx="11.5" cy="3.5" r="2" />
      <line x1="4.5" y1="5.5" x2="4.5" y2="9.5" />
      <path d="M11.5 5.5v1.2a2.8 2.8 0 0 1-2.8 2.8H6.5" />
    </>
  ),
  /* ---- table & surface affordances ------------------------------------ */
  plus: (
    <>
      <line x1="7.5" y1="2.5" x2="7.5" y2="12.5" />
      <line x1="2.5" y1="7.5" x2="12.5" y2="7.5" />
    </>
  ),
  minus: <line x1="2.5" y1="7.5" x2="12.5" y2="7.5" />,
  search: (
    <>
      <circle cx="6.5" cy="6.5" r="4.5" />
      <line x1="9.9" y1="9.9" x2="13.5" y2="13.5" />
    </>
  ),
  'zoom-in': (
    <>
      <circle cx="6.5" cy="6.5" r="4.5" />
      <line x1="9.9" y1="9.9" x2="13.5" y2="13.5" />
      <line x1="6.5" y1="4.5" x2="6.5" y2="8.5" />
      <line x1="4.5" y1="6.5" x2="8.5" y2="6.5" />
    </>
  ),
  'zoom-out': (
    <>
      <circle cx="6.5" cy="6.5" r="4.5" />
      <line x1="9.9" y1="9.9" x2="13.5" y2="13.5" />
      <line x1="4.5" y1="6.5" x2="8.5" y2="6.5" />
    </>
  ),
  filter: (
    <polygon points="1.5,2.5 13.5,2.5 9.5,8 9.5,13.5 5.5,11 5.5,8" />
  ),
  sort: (
    <>
      <polyline points="2.5,5.5 4.5,3.5 6.5,5.5" />
      <line x1="4.5" y1="3.5" x2="4.5" y2="12.5" />
      <polyline points="8.5,9.5 10.5,11.5 12.5,9.5" />
      <line x1="10.5" y1="11.5" x2="10.5" y2="2.5" />
    </>
  ),
  'arrow-up': (
    <>
      <line x1="7.5" y1="13" x2="7.5" y2="2" />
      <polyline points="3.5,6 7.5,2 11.5,6" />
    </>
  ),
  'arrow-down': (
    <>
      <line x1="7.5" y1="2" x2="7.5" y2="13" />
      <polyline points="3.5,9 7.5,13 11.5,9" />
    </>
  ),
  'arrow-left': (
    <>
      <line x1="13" y1="7.5" x2="2" y2="7.5" />
      <polyline points="6,3.5 2,7.5 6,11.5" />
    </>
  ),
  'arrow-right': (
    <>
      <line x1="2" y1="7.5" x2="13" y2="7.5" />
      <polyline points="9,3.5 13,7.5 9,11.5" />
    </>
  ),
  'chevron-up': <polyline points="3.5,10 7.5,6 11.5,10" />,
  'chevron-down': <polyline points="3.5,5 7.5,9 11.5,5" />,
  'chevron-left': <polyline points="10,3.5 6,7.5 10,11.5" />,
  'chevron-right': <polyline points="5,3.5 9,7.5 5,11.5" />,
  'more-horizontal': (
    <>
      <circle cx="2.5" cy="7.5" r="1.05" {...D} />
      <circle cx="7.5" cy="7.5" r="1.05" {...D} />
      <circle cx="12.5" cy="7.5" r="1.05" {...D} />
    </>
  ),
  'more-vertical': (
    <>
      <circle cx="7.5" cy="2.5" r="1.05" {...D} />
      <circle cx="7.5" cy="7.5" r="1.05" {...D} />
      <circle cx="7.5" cy="12.5" r="1.05" {...D} />
    </>
  ),
  drag: (
    <>
      <circle cx="5.5" cy="2.5" r="1.05" {...D} />
      <circle cx="9.5" cy="2.5" r="1.05" {...D} />
      <circle cx="5.5" cy="7.5" r="1.05" {...D} />
      <circle cx="9.5" cy="7.5" r="1.05" {...D} />
      <circle cx="5.5" cy="12.5" r="1.05" {...D} />
      <circle cx="9.5" cy="12.5" r="1.05" {...D} />
    </>
  ),
  'external-link': (
    <>
      <polyline points="9,2.5 13.5,2.5 13.5,7" />
      <line x1="13.5" y1="2.5" x2="7.5" y2="8.5" />
      <path d="M11.5 9.5v3a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h3" />
    </>
  ),
  copy: (
    <>
      <rect x="5.5" y="5.5" width="8" height="8" />
      <path d="M10.5 5.5v-3a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h3" />
    </>
  ),
  trash: (
    <>
      <line x1="1.5" y1="4.5" x2="13.5" y2="4.5" />
      <path d="M3.5 4.5v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-8" />
      <path d="M5.5 4.5v-2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
    </>
  ),
  edit: (
    <>
      <path d="M2.5 10.5 10.5 2.5l3 3-8 8-3.8.8Z" />
      <line x1="8.8" y1="4.2" x2="11.8" y2="7.2" />
    </>
  ),
  eye: (
    <>
      <path d="M1.5 7.5C3 5 5.2 3 7.5 3s4.5 2 6 4.5c-1.5 2.5-3.7 4.5-6 4.5s-4.5-2-6-4.5Z" />
      <circle cx="7.5" cy="7.5" r="2" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M1.5 7.5C3 5 5.2 3 7.5 3s4.5 2 6 4.5c-1.5 2.5-3.7 4.5-6 4.5s-4.5-2-6-4.5Z" />
      <circle cx="7.5" cy="7.5" r="2" />
    </>
  ),
  /* Sliders, drawn with straight strokes only so all six edges stay crisp. */
  settings: (
    <>
      <line x1="1.5" y1="3.5" x2="13.5" y2="3.5" />
      <line x1="1.5" y1="7.5" x2="13.5" y2="7.5" />
      <line x1="1.5" y1="11.5" x2="13.5" y2="11.5" />
      <line x1="10.5" y1="1.5" x2="10.5" y2="5.5" />
      <line x1="4.5" y1="5.5" x2="4.5" y2="9.5" />
      <line x1="9.5" y1="9.5" x2="9.5" y2="13.5" />
    </>
  ),
  info: (
    <>
      <circle cx="7.5" cy="7.5" r="6" />
      <line x1="7.5" y1="7" x2="7.5" y2="11" />
      <circle cx="7.5" cy="4.6" r="1" {...D} />
    </>
  ),
  calendar: (
    <>
      <rect x="1.5" y="3.5" width="12" height="10" />
      <line x1="1.5" y1="6.5" x2="13.5" y2="6.5" />
      <line x1="4.5" y1="1.5" x2="4.5" y2="4" />
      <line x1="10.5" y1="1.5" x2="10.5" y2="4" />
    </>
  ),
  user: (
    <>
      <circle cx="7.5" cy="5" r="2.75" />
      <path d="M2.5 13.5a5 5 0 0 1 10 0" />
    </>
  ),
  logout: (
    <>
      <path d="M6.5 13.5h-4a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1h4" />
      <polyline points="10,4.5 13,7.5 10,10.5" />
      <line x1="13" y1="7.5" x2="5.5" y2="7.5" />
    </>
  ),
  save: (
    <>
      <path d="M2.5 2.5h8.2l2.8 2.8v8.2h-11Z" />
      <polyline points="4.5,2.5 4.5,6.5 10.5,6.5 10.5,2.5" />
      <rect x="4.5" y="9.5" width="6" height="4" />
    </>
  ),
  sun: (
    <>
      <circle cx="7.5" cy="7.5" r="3" />
      <line x1="7.5" y1="1" x2="7.5" y2="2.5" />
      <line x1="7.5" y1="12.5" x2="7.5" y2="14" />
      <line x1="1" y1="7.5" x2="2.5" y2="7.5" />
      <line x1="12.5" y1="7.5" x2="14" y2="7.5" />
      <line x1="2.9" y1="2.9" x2="4" y2="4" />
      <line x1="11" y1="11" x2="12.1" y2="12.1" />
      <line x1="2.9" y1="12.1" x2="4" y2="11" />
      <line x1="11" y1="4" x2="12.1" y2="2.9" />
    </>
  ),
  moon: <path d="M13.4 8.6A6 6 0 1 1 6.9 2a4.7 4.7 0 0 0 6.5 6.6Z" />,
} as const;

/* ==========================================================================
   NEGATION — one mechanism, five glyphs.

   A "no signal" glyph is its own glyph with a 45° slash across it. Drawn
   naively that slash lands within a pixel of the strokes it crosses and the
   whole thing turns into a 3px smudge — which is what killed the previous
   antenna-off, link-off, eye-off, signal-off and video-off. So the base glyph
   is masked: a 3.6px black line cuts a real gap out of it, and the 1.25px
   slash is then drawn into that gap. The negated glyph is the affirmative
   glyph, unedited, plus a cut — which is why the pair always reads as a pair.

   The slash runs ACROSS the glyph's dominant axis. `signal` climbs to the
   right, so its slash falls to the right; a chain that runs up-right is cut
   down-right. Getting this backwards is what makes a struck icon read as an
   arrow.
   ========================================================================== */
type Slash = 'ne' | 'se';

const STRUCK: Partial<Record<IconName, Slash>> = {
  'signal-off': 'se',
  'antenna-off': 'ne',
  'link-off': 'se',
  'video-off': 'ne',
  'eye-off': 'ne',
};

/** Endpoints of the one slash the set owns, in each direction. */
const SLASH: Record<Slash, { x1: number; y1: number; x2: number; y2: number }> = {
  ne: { x1: 2.5, y1: 12.5, x2: 12.5, y2: 2.5 },
  se: { x1: 2.5, y1: 2.5, x2: 12.5, y2: 12.5 },
};

export type IconName = keyof typeof PATHS;

/**
 * TIER 2. Exhaustive — the only glyphs in the set drawn as fills. Adding a
 * name here is a design decision, not a convenience: it means the outline
 * would destroy the meaning, which is true of transport marks and of nothing
 * else we have found.
 */
const SOLID = new Set<IconName>(['play', 'pause', 'stop', 'record']);

/** Every icon name, in declaration order — for gallery / audit surfaces. */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];

/** The solid tier, exported so the specimen can prove the rule holds. */
export const SOLID_ICONS: ReadonlySet<IconName> = SOLID;

interface IconProps {
  name: IconName;
  /** Display size in px. The grid is drawn for 16; 12–20 stay crisp. */
  size?: number;
  /**
   * Accessible name. Provide it when the icon is the only content of a
   * control; omit it when adjacent text already names the thing (the icon is
   * then marked aria-hidden so a screen reader does not read it twice).
   */
  title?: string;
  className?: string;
}

export function Icon({ name, size = 16, title, className }: IconProps) {
  const solid = SOLID.has(name);
  const slash = STRUCK[name];
  const maskId = useId();
  const body =
    slash === undefined ? (
      PATHS[name]
    ) : (
      <>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
          <rect x="0" y="0" width="16" height="16" fill="#fff" />
          <line {...SLASH[slash]} stroke="#000" strokeWidth={3.6} strokeLinecap="butt" />
        </mask>
        <g mask={`url(#${maskId})`}>{PATHS[name]}</g>
        <line {...SLASH[slash]} />
      </>
    );
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={solid ? 'currentColor' : 'none'}
      stroke={solid ? 'none' : 'currentColor'}
      strokeWidth={solid ? undefined : 1.25}
      strokeLinecap={solid ? undefined : 'round'}
      strokeLinejoin={solid ? undefined : 'round'}
      role={title === undefined ? undefined : 'img'}
      aria-label={title}
      aria-hidden={title === undefined ? true : undefined}
      focusable="false"
      shapeRendering="geometricPrecision"
    >
      {title !== undefined && <title>{title}</title>}
      {body}
    </svg>
  );
}
